import { useCallback, useEffect, useState } from "react";
import {
  downloadAdminDataResourcesExport,
  getAdminDataResourceDetail,
  getAdminDataResources,
  type AdminDataResource,
  type AdminDataResourceDetail,
} from "../api/adminClient";
import AdminAssetImage from "../components/AdminAssetImage";

const inputClass =
  "px-4 py-2.5 border border-[var(--color-outline-variant)] rounded-lg text-sm bg-[var(--color-surface)] text-[var(--color-on-surface)] focus:outline-none focus:border-[var(--color-accent-blue)] focus:ring-1 focus:ring-[var(--color-accent-blue)] transition-colors transition-shadow duration-150";

const selectClass = inputClass;
const thClass = "pb-3 pr-4 font-medium text-left text-[var(--color-on-surface-variant)]";
const tdClass = "py-3 pr-4 text-[var(--color-on-surface)] align-middle";

function requestTypeLabel(t: string) {
  const map: Record<string, string> = {
    embed_image: "图片向量化",
    embed_text: "文本向量化",
    embed_fused: "融合向量化",
    ocr: "OCR 识别",
  };
  return map[t] || t;
}

function fmtBytes(n: number) {
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  if (n >= 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${n} B`;
}

interface DataResourcesPageProps {
  onAuthError?: () => void;
}

export default function DataResourcesPage({ onAuthError }: DataResourcesPageProps) {
  const [items, setItems] = useState<AdminDataResource[]>([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [userIdFilter, setUserIdFilter] = useState("");
  const [requestType, setRequestType] = useState("");
  const [contentType, setContentType] = useState("");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<AdminDataResourceDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const uid = userIdFilter.trim() ? parseInt(userIdFilter.trim(), 10) : undefined;
      const data = await getAdminDataResources({
        page,
        limit: 50,
        user_id: uid && !Number.isNaN(uid) ? uid : undefined,
        request_type: requestType || undefined,
        content_type: contentType || undefined,
        q: search || undefined,
      });
      setItems(data.items ?? []);
      setPages(data.pages);
      setTotal(data.total);
    } catch (e: unknown) {
      setItems([]);
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      if (msg.includes("401") || msg.includes("403") || msg.includes("管理员")) {
        onAuthError?.();
      }
    } finally {
      setLoading(false);
    }
  }, [page, userIdFilter, requestType, contentType, search, onAuthError]);

  useEffect(() => {
    load();
  }, [load]);

  const openDetail = async (id: number) => {
    setSelectedId(id);
    setDetail(null);
    setDetailLoading(true);
    try {
      const data = await getAdminDataResourceDetail(id);
      setDetail(data);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setDetailLoading(false);
    }
  };

  const handleExport = async (format: "json" | "csv") => {
    setExporting(true);
    setError(null);
    try {
      const uid = userIdFilter.trim() ? parseInt(userIdFilter.trim(), 10) : undefined;
      await downloadAdminDataResourcesExport({
        user_id: uid && !Number.isNaN(uid) ? uid : undefined,
        request_type: requestType || undefined,
        content_type: contentType || undefined,
        q: search || undefined,
        format,
      });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1
            className="text-[28px] font-semibold text-[var(--color-on-surface)] mb-1"
            style={{ letterSpacing: "-0.02em" }}
          >
            数据资源库
          </h1>
          <p className="text-[15px] text-[var(--color-on-surface-variant)]">
            统筹查看所有用户的处理内容与原始资源，支持筛选与导出
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => handleExport("json")}
            disabled={exporting || loading}
            className="px-4 py-2.5 rounded-lg text-sm font-medium bg-[var(--color-surface-container-low)] disabled:opacity-50"
          >
            导出 JSON
          </button>
          <button
            type="button"
            onClick={() => handleExport("csv")}
            disabled={exporting || loading}
            className="px-4 py-2.5 rounded-lg text-sm font-medium bg-[var(--color-secondary-container)] text-white disabled:opacity-50"
          >
            {exporting ? "导出中..." : "导出 CSV"}
          </button>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="px-4 py-2.5 rounded-lg text-sm font-medium bg-[var(--color-surface-container-low)] disabled:opacity-50"
          >
            {loading ? "加载中..." : "刷新"}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-[var(--color-error-container)] text-[var(--color-on-error-container)] rounded-xl text-sm">
          {error}
        </div>
      )}

      <div className="bg-[var(--color-surface)] rounded-2xl p-6 border border-[var(--color-border-subtle)]">
        <form
          className="flex flex-col lg:flex-row flex-wrap gap-3 mb-6"
          onSubmit={(e) => {
            e.preventDefault();
            setSearch(searchInput);
            setPage(1);
          }}
        >
          <input
            type="text"
            placeholder="搜索用户名或文本内容..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className={`${inputClass} lg:flex-1 min-w-[200px]`}
          />
          <input
            type="text"
            placeholder="用户 ID"
            value={userIdFilter}
            onChange={(e) => {
              setUserIdFilter(e.target.value);
              setPage(1);
            }}
            className={`${inputClass} w-32`}
          />
          <select
            value={requestType}
            onChange={(e) => {
              setRequestType(e.target.value);
              setPage(1);
            }}
            className={selectClass}
          >
            <option value="">全部处理类型</option>
            <option value="embed_image">图片向量化</option>
            <option value="embed_text">文本向量化</option>
            <option value="embed_fused">融合向量化</option>
            <option value="ocr">OCR</option>
          </select>
          <select
            value={contentType}
            onChange={(e) => {
              setContentType(e.target.value);
              setPage(1);
            }}
            className={selectClass}
          >
            <option value="">全部资源类型</option>
            <option value="image">图片</option>
            <option value="text">文本</option>
          </select>
          <button
            type="submit"
            className="px-4 py-2.5 rounded-lg text-sm font-medium bg-[var(--color-secondary-container)] text-white"
          >
            搜索
          </button>
        </form>

        {total > 0 && (
          <p className="mb-4 text-sm text-[var(--color-on-surface-variant)]">共 {total} 条资源记录</p>
        )}

        {items.length === 0 ? (
          <div className="py-12 text-center text-sm text-[var(--color-on-surface-variant)]">
            {loading ? "加载中..." : "暂无数据资源"}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-[var(--color-border-subtle)]">
                  <th className={thClass}>用户</th>
                  <th className={thClass}>类型</th>
                  <th className={thClass}>内容</th>
                  <th className={thClass}>模型</th>
                  <th className={thClass}>时间</th>
                  <th className={thClass}></th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr
                    key={row.id}
                    className="border-b border-[var(--color-border-subtle)] last:border-0 hover:bg-[var(--color-surface-container-low)]"
                  >
                    <td className={tdClass}>
                      <div className="font-medium">{row.username}</div>
                      <div className="text-xs text-[var(--color-outline)]">#{row.user_id}</div>
                    </td>
                    <td className={tdClass}>
                      <div>{requestTypeLabel(row.request_type)}</div>
                      <div className="text-xs text-[var(--color-outline)]">{row.content_type || "—"}</div>
                    </td>
                    <td className={`${tdClass} max-w-sm`}>
                      {row.has_image && row.content_hash ? (
                        <div className="flex items-start gap-3">
                          <AdminAssetImage contentHash={row.content_hash} />
                          <div className="min-w-0">
                            {row.text_preview ? (
                              <div className="text-xs whitespace-pre-wrap break-all line-clamp-3">
                                {row.text_preview}
                              </div>
                            ) : (
                              <div className="text-xs text-[var(--color-outline)]">
                                {row.byte_size ? fmtBytes(row.byte_size) : row.content_hash.slice(0, 12) + "…"}
                              </div>
                            )}
                            {row.auxiliary_preview ? (
                              <div className="mt-1 text-xs text-[var(--color-on-surface-variant)] line-clamp-2">
                                辅助: {row.auxiliary_preview}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      ) : row.text_preview ? (
                        <div className="p-2 rounded-lg bg-[var(--color-surface-container-low)] text-xs whitespace-pre-wrap break-all line-clamp-4 font-mono">
                          {row.text_preview}
                        </div>
                      ) : (
                        <span className="text-xs text-[var(--color-outline)]">—</span>
                      )}
                    </td>
                    <td className={`${tdClass} font-mono text-xs`}>
                      {row.provider && row.model ? `${row.provider}/${row.model}` : "—"}
                    </td>
                    <td className={`${tdClass} text-xs text-[var(--color-on-surface-variant)]`}>
                      {row.created_at?.replace("T", " ").slice(0, 19) || "—"}
                    </td>
                    <td className={tdClass}>
                      <button
                        type="button"
                        onClick={() => openDetail(row.id)}
                        className="text-xs px-2 py-1 rounded bg-[var(--color-surface-container-low)]"
                      >
                        详情
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            <button
              type="button"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-3 py-1.5 rounded-lg text-sm bg-[var(--color-surface-container-low)] disabled:opacity-40"
            >
              上一页
            </button>
            <span className="text-sm text-[var(--color-on-surface-variant)] tabular-nums">
              {page} / {pages}
            </span>
            <button
              type="button"
              disabled={page >= pages || loading}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1.5 rounded-lg text-sm bg-[var(--color-surface-container-low)] disabled:opacity-40"
            >
              下一页
            </button>
          </div>
        )}
      </div>

      {selectedId !== null && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => {
              setSelectedId(null);
              setDetail(null);
            }}
            aria-hidden
          />
          <div className="relative w-full max-w-2xl h-full bg-[var(--color-surface)] border-l border-[var(--color-border-subtle)] shadow-2xl overflow-y-auto">
            <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-[var(--color-border-subtle)] bg-[var(--color-surface)]">
              <h2 className="text-lg font-semibold">资源详情 #{selectedId}</h2>
              <button
                type="button"
                onClick={() => {
                  setSelectedId(null);
                  setDetail(null);
                }}
                className="p-2 rounded-lg hover:bg-[var(--color-surface-container-low)]"
              >
                ✕
              </button>
            </div>
            <div className="p-6 space-y-4 text-sm">
              {detailLoading ? (
                <div className="py-12 text-center text-[var(--color-on-surface-variant)]">加载中...</div>
              ) : detail ? (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <span className="text-[var(--color-on-surface-variant)]">用户: </span>
                      {detail.username} (#{detail.user_id})
                    </div>
                    <div>
                      <span className="text-[var(--color-on-surface-variant)]">类型: </span>
                      {requestTypeLabel(detail.request_type)}
                    </div>
                    <div>
                      <span className="text-[var(--color-on-surface-variant)]">时间: </span>
                      {detail.created_at}
                    </div>
                    <div>
                      <span className="text-[var(--color-on-surface-variant)]">哈希: </span>
                      <span className="font-mono text-xs">{detail.content_hash?.slice(0, 16)}…</span>
                    </div>
                  </div>
                  {detail.has_image && detail.content_hash ? (
                    <div>
                      <div className="text-[var(--color-on-surface-variant)] mb-2">图片预览</div>
                      <AdminAssetImage
                        contentHash={detail.content_hash}
                        thumbClassName="max-w-full max-h-64 w-auto h-auto"
                      />
                    </div>
                  ) : null}
                  {detail.text_body ? (
                    <div>
                      <div className="text-[var(--color-on-surface-variant)] mb-2">文本内容</div>
                      <pre className="p-4 rounded-xl bg-[var(--color-surface-container-low)] text-xs whitespace-pre-wrap break-all max-h-[50vh] overflow-y-auto font-mono">
                        {detail.text_body}
                      </pre>
                    </div>
                  ) : null}
                  {detail.auxiliary_snapshot ? (
                    <div>
                      <div className="text-[var(--color-on-surface-variant)] mb-2">辅助文本（OCR / 融合）</div>
                      <pre className="p-4 rounded-xl bg-[var(--color-surface-container-low)] text-xs whitespace-pre-wrap break-all max-h-[40vh] overflow-y-auto font-mono">
                        {detail.auxiliary_snapshot}
                      </pre>
                    </div>
                  ) : null}
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
